/**
 * triggerRegistry — Open/Closed Principle entry point for the trigger pipeline.
 *
 * Adding a new event later requires exactly 2 lines elsewhere:
 *   1. `GlobalBus.$emit('foo.bar', payload)` in the emitting module.
 *   2. `registerHandler('foo.bar', myHandler)` in this file's `boot()`.
 *
 * Default handler: `_handleLeadMoveStageToWon` — fired after a CRM lead is
 * dragged to a Won-keyword column. It hits the backend for active mappings
 * and always dispatches the suggester modal via the global event bus: when
 * there are mappings the user picks one and the wizard opens; when there
 * are 0 mappings the modal shows an onboarding empty state explaining how
 * to configure templates (UC-T04 atualizado — empty state informativo).
 *
 * Boot semantics:
 *   - `_booted` guards against duplicate registration on re-login.
 *   - `boot()` is invoked from the post-login bootstrap (services/parameters.js)
 *     so we never register handlers before the user session is available.
 */
import { GlobalBus } from '@services/bus'
import { MODAL } from '@constants/Text'
import { apiTarefas } from '@modules/tarefas/api/apiTarefas'
import { TRIGGER_EVENTS } from '@modules/tarefas/constants/triggerEvents'

/**
 * Keyword set used to detect a "Won/Ganho" column by name (the backend
 * `crm_funil_vendas_etapa` has no `tipo` / `is_won` flag — see
 * usecase_crm_calendar.py:1604 for the canonical list).
 *
 * Trade-off documented in PR: if a gestor renames their Won column to
 * "Vencido" the trigger silently won't fire. Future v2.1 may add an explicit
 * `is_terminal_ganho` column on the funil-etapa schema.
 */
export const WON_KEYWORDS = [
  'ganho',
  'ganha',
  'fechado',
  'fechada',
  'convertido',
  'convertida',
]

/**
 * Returns true when the given stage's name matches one of the WON keywords.
 * Case-insensitive and trim-safe.
 */
export function isWonStage(stage) {
  if (!stage || !stage.nome) return false
  const normalized = String(stage.nome).toLowerCase().trim()
  return WON_KEYWORDS.some(function (kw) { return normalized.includes(kw) })
}

// Internal registry — keys are eventType, values are handler functions.
const handlers = {}
let _booted = false

/**
 * Subscribes a handler to a trigger event. Multiple handlers for the same
 * event are allowed and fire in registration order (Vue.$on semantics).
 *
 * @param {string} eventType
 * @param {Function} handlerFn
 */
export function registerHandler(eventType, handlerFn) {
  if (!eventType || typeof handlerFn !== 'function') return
  if (!handlers[eventType]) handlers[eventType] = []
  handlers[eventType].push(handlerFn)
  GlobalBus.$on(eventType, handlerFn)
}

/**
 * Default handler for the CRM drag-to-Won event. Looks up active mappings
 * for the office; if none → silent return (no modal). Otherwise → emits
 * the suggester modal with mappings + interpolation vars.
 *
 * Errors are swallowed silently per UC-T04 / AC-09 — a missing trigger
 * configuration is not a failure mode for the user.
 */
async function _handleLeadMoveStageToWon(payload) {
  let r
  try {
    r = await apiTarefas.getActiveTriggerMappingsByEvent({
      eventType: TRIGGER_EVENTS.LEAD_MOVE_STAGE_TO_WON,
    })
  } catch {
    // Defensive: even on network/parse error we stay silent (UC-T04 spirit).
    return
  }
  if (!r || r.status !== 200) return
  const mappings = r.value || []

  // Always open the modal — even with 0 mappings so the user sees the onboarding
  // state and learns how to configure templates (UC-T04 updated: empty state is
  // informative, not silent).
  // `onClose` é repassado no topo (o modal lê `data.onClose`, não `vars.onClose`)
  // — sem isso o callback do emissor (ex: prompt "acessar visão cliente" do CRM
  // ao converter um lead) nunca dispara.
  GlobalBus.$emit(MODAL.PM_TRIGGER_SUGGESTER, {
    mappings: mappings,
    vars: payload || {},
    onClose: (payload && payload.onClose) || null,
  })
}

/**
 * Boots the registry — registers every default handler.
 * Idempotent: subsequent calls (e.g. re-login) are no-ops.
 */
export function boot() {
  if (_booted) return
  _booted = true
  registerHandler(TRIGGER_EVENTS.LEAD_MOVE_STAGE_TO_WON, _handleLeadMoveStageToWon)
}

/**
 * Test helper — resets `_booted` and clears the in-memory handler list so
 * unit tests can re-boot the registry between cases. Production code should
 * NEVER call this.
 */
export function _resetForTests() {
  Object.keys(handlers).forEach(function (eventType) {
    handlers[eventType].forEach(function (fn) {
      GlobalBus.$off(eventType, fn)
    })
    delete handlers[eventType]
  })
  _booted = false
}

export default {
  registerHandler: registerHandler,
  boot: boot,
  isWonStage: isWonStage,
  WON_KEYWORDS: WON_KEYWORDS,
  _resetForTests: _resetForTests,
}
